import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { Sparkles, Download, FileText, Edit, Save, X, FileCheck, Upload, Building2, Image, AlertCircle } from 'lucide-react'
import api from '../lib/api'
import toast from 'react-hot-toast'

const ProposalGenerator = () => {
  const location = useLocation()
  const [generating, setGenerating] = useState(false)
  const [proposal, setProposal] = useState(null)
  const [editing, setEditing] = useState(false)
  const [templates, setTemplates] = useState([])
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const logoInputRef = useRef(null)
  const [tenderId, setTenderId] = useState(null)
  const [tenderContext, setTenderContext] = useState(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    organization: '',
    budget: '',
    timeline: '',
    requirements: ''
  })
  const [selectedModel, setSelectedModel] = useState('gpt-3.5-turbo')
  const [companyInfo, setCompanyInfo] = useState({
    name: '',
    logo: '',
    logoPreview: '',
    address: '',
    contact: '',
    website: '',
    confidentialityClause: 'This document contains confidential information.'
  })

  useEffect(() => {
    fetchTemplates()
    // Load saved company info from localStorage
    const savedCompanyInfo = localStorage.getItem('proposalCompanyInfo')
    if (savedCompanyInfo) {
      try {
        setCompanyInfo(JSON.parse(savedCompanyInfo))
      } catch (e) {
        console.error('Failed to load saved company info')
      }
    }

    // Auto-fill form if coming from tender details page
    if (location.state?.tenderData) {
      const { tenderData } = location.state
      setTenderId(location.state.tenderId)
      setTenderContext(tenderData.aiContext)
      setFormData({
        title: tenderData.title || '',
        description: tenderData.description || '',
        organization: tenderData.organization || '',
        budget: tenderData.budget || '',
        timeline: tenderData.timeline || '',
        requirements: tenderData.requirements || ''
      })
      toast.success('Form auto-filled from tender details. Review and generate!')
    }
  }, [location.state])

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/templates', { params: { isActive: true } })
      setTemplates(response.data || [])
      // Auto-select default template if available
      const defaultTemplate = response.data?.find(t => t.isDefault)
      if (defaultTemplate) {
        setSelectedTemplateId(defaultTemplate._id)
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error)
    }
  }

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Show preview immediately
    const reader = new FileReader()
    reader.onload = (e) => {
      setCompanyInfo(prev => ({ ...prev, logoPreview: e.target.result }))
    }
    reader.readAsDataURL(file)

    // Upload to server
    setUploadingLogo(true)
    try {
      const formData = new FormData()
      formData.append('logo', file)

      const response = await api.post('/proposals/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      setCompanyInfo(prev => ({ 
        ...prev, 
        logo: response.data.logo.url,
        logoPreview: prev.logoPreview 
      }))
      toast.success('Logo uploaded successfully!')
    } catch (error) {
      console.error('Logo upload failed:', error)
      toast.error('Failed to upload logo')
      setCompanyInfo(prev => ({ ...prev, logoPreview: '' }))
    } finally {
      setUploadingLogo(false)
    }
  }

  const saveCompanyInfo = () => {
    localStorage.setItem('proposalCompanyInfo', JSON.stringify(companyInfo))
    toast.success('Company info saved for future proposals')
  }

  const handleGenerate = async (e) => {
    e.preventDefault()
    setGenerating(true)

    try {
      const response = await api.post('/proposals/generate', {
        ...formData,
        templateId: selectedTemplateId || undefined,
        tenderId: tenderId || undefined,
        tenderContext: tenderContext || undefined,
        aiModel: selectedModel,
        companyInfo: {
          name: companyInfo.name,
          logo: companyInfo.logo,
          address: companyInfo.address,
          contact: companyInfo.contact,
          website: companyInfo.website,
          confidentialityClause: companyInfo.confidentialityClause
        }
      })
      
      // Background processing - show success and redirect to proposals list
      if (response.status === 202) {
        toast.success('Proposal generation started! You will be notified when ready.')
        // Reset form
        setFormData({
          title: '',
          description: '',
          organization: '',
          budget: '',
          timeline: '',
          requirements: ''
        })
        setTenderId(null)
        setTenderContext(null)
      } else {
        // Fallback for direct response
        setProposal(response.data)
        setEditing(false)
        toast.success('Proposal generated successfully!')
      }
    } catch (error) {
      console.error('Failed to generate proposal:', error)
      toast.error(error.response?.data?.error || 'Failed to generate proposal. Please try again.')
    } finally {
      setGenerating(false)
    }
  }

  const handleEdit = (section, value) => {
    setProposal(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.title === section ? { ...s, content: value } : s
      )
    }))
  }

  const handleDownloadPDF = async () => {
    try {
      const response = await api.post('/proposals/download/pdf', 
        { proposalId: proposal._id },
        { responseType: 'blob' }
      )
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${proposal.title || 'proposal'}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      console.error('Download failed:', error)
      // Fallback: Generate PDF client-side
      handleDownloadSimple()
    }
  }

  const handleDownloadSimple = () => {
    // Simple text download as fallback
    let content = `${proposal.title}\n\n`
    
    if (proposal.executiveSummary) {
      content += `EXECUTIVE SUMMARY\n${proposal.executiveSummary}\n\n`
    }
    
    proposal.sections?.forEach(section => {
      content += `${section.title.toUpperCase()}\n${section.content}\n\n`
    })
    
    if (proposal.budget) {
      content += `BUDGET\n${proposal.budget.currency} ${proposal.budget.amount}\n\n`
    }
    
    const blob = new Blob([content], { type: 'text/plain' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${proposal.title || 'proposal'}.txt`
    link.click()
  }

  const handleDownloadWord = () => {
    // Generate Word-compatible HTML with company branding
    const logoHtml = proposal.companyInfo?.logo 
      ? `<img src="${window.location.origin}${proposal.companyInfo.logo}" style="max-height:80px;margin-bottom:20px;" /><br/>`
      : '';
    
    const companyHeader = `
      <div style="border-bottom:2px solid #333;padding-bottom:20px;margin-bottom:30px;">
        ${logoHtml}
        ${proposal.companyInfo?.name ? `<p style="font-size:18px;font-weight:bold;margin:0;">${proposal.companyInfo.name}</p>` : ''}
        ${proposal.companyInfo?.address ? `<p style="font-size:12px;color:#666;margin:5px 0;">${proposal.companyInfo.address}</p>` : ''}
        ${proposal.companyInfo?.contact ? `<p style="font-size:12px;color:#666;margin:5px 0;">${proposal.companyInfo.contact}</p>` : ''}
        ${proposal.companyInfo?.website ? `<p style="font-size:12px;color:#0066cc;margin:5px 0;">${proposal.companyInfo.website}</p>` : ''}
      </div>
    `;

    const coverInfo = `
      <p style="text-align:right;font-size:12px;color:#666;">
        Date: ${new Date(proposal.coverPage?.date || proposal.createdAt).toLocaleDateString()}<br/>
        ${proposal.clientInfo?.name ? `Submitted to: <strong>${proposal.clientInfo.name}</strong>` : ''}
      </p>
    `;

    let htmlContent = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head>
        <meta charset='utf-8'>
        <title>${proposal.title}</title>
        <style>
          body { font-family: 'Plus Jakarta Sans', Arial, sans-serif; font-size: 12pt; line-height: 1.6; }
          h1 { font-size: 24pt; color: #1a1a1a; margin-bottom: 20px; }
          h2 { font-size: 16pt; color: #333; margin-top: 30px; margin-bottom: 15px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
          p { margin: 10px 0; }
        </style>
      </head>
      <body>
        ${companyHeader}
        ${coverInfo}
        <h1>${proposal.title}</h1>
        ${proposal.executiveSummary ? `<h2>Executive Summary</h2><p>${proposal.executiveSummary.replace(/\n/g, '<br>')}</p>` : ''}
        ${proposal.sections?.map(s => `
          <h2>${s.title}</h2>
          <p>${(s.content || '').replace(/\n/g, '<br>')}</p>
        `).join('')}
        ${proposal.budget ? `<h2>Investment</h2><p style="font-size:18pt;color:#0066cc;font-weight:bold;">${proposal.budget.currency} ${proposal.budget.amount?.toLocaleString()}</p>` : ''}
        ${proposal.timeline ? `<h2>Project Timeline</h2><p>${proposal.timeline}</p>` : ''}
        ${proposal.companyInfo?.confidentialityClause ? `<hr style="margin-top:50px;"/><p style="font-size:10pt;color:#999;text-align:center;">${proposal.companyInfo.confidentialityClause}</p>` : ''}
      </body>
      </html>
    `
    
    const blob = new Blob([htmlContent], { type: 'application/msword' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${proposal.title || 'proposal'}.doc`
    link.click()
  }

  const handleSave = async () => {
    try {
      await api.put(`/proposals/${proposal._id}`, proposal)
      toast.success('Proposal saved successfully!')
      setEditing(false)
    } catch (error) {
      console.error('Save failed:', error)
      toast.error('Failed to save proposal')
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">AI Proposal Generator</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Create professional proposals in minutes</p>
      </div>

      {/* Generation Form */}
      {!proposal && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <form onSubmit={handleGenerate} className="space-y-6">
            {/* Tender Context Banner */}
            {tenderId && tenderContext && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-green-800">Auto-filled from Tender</h4>
                    <p className="text-sm text-green-700 mt-1">
                      Form has been pre-filled with tender details. Review the information below and make any changes before generating.
                    </p>
                    {tenderContext.requirements?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-green-800">Key Requirements from Tender:</p>
                        <ul className="text-xs text-green-700 list-disc list-inside mt-1">
                          {tenderContext.requirements.slice(0, 3).map((req, i) => (
                            <li key={i}>{req}</li>
                          ))}
                          {tenderContext.requirements.length > 3 && (
                            <li>...and {tenderContext.requirements.length - 3} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Company Information Section */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
                  Company Information
                </h3>
                <button
                  type="button"
                  onClick={saveCompanyInfo}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save for later
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Logo Upload */}
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Company Logo
                  </label>
                  <div 
                    className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center hover:border-blue-500 transition-colors cursor-pointer bg-white dark:bg-gray-700"
                    onClick={() => logoInputRef.current?.click()}
                  >
                    <input
                      type="file"
                      ref={logoInputRef}
                      onChange={handleLogoUpload}
                      accept=".png,.jpg,.jpeg,.svg,.webp"
                      className="hidden"
                    />
                    {companyInfo.logoPreview || companyInfo.logo ? (
                      <div className="relative">
                        <img 
                          src={companyInfo.logoPreview || companyInfo.logo} 
                          alt="Company Logo" 
                          className="max-h-20 mx-auto object-contain"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Click to change</p>
                      </div>
                    ) : (
                      <>
                        <Image className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">PNG, JPG, SVG</p>
                      </>
                    )}
                  </div>
                </div>

                {/* Company Details */}
                <div className="md:col-span-2 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company Name</label>
                    <input
                      type="text"
                      value={companyInfo.name}
                      onChange={(e) => setCompanyInfo({...companyInfo, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Your Company Name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact</label>
                      <input
                        type="text"
                        value={companyInfo.contact}
                        onChange={(e) => setCompanyInfo({...companyInfo, contact: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Email or Phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Website</label>
                      <input
                        type="text"
                        value={companyInfo.website}
                        onChange={(e) => setCompanyInfo({...companyInfo, website: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="www.company.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address</label>
                    <input
                      type="text"
                      value={companyInfo.address}
                      onChange={(e) => setCompanyInfo({...companyInfo, address: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Company Address"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Project Details Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Project Details</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Proposal Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="e.g., Cloud Migration Services Proposal"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Organization/Client *
              </label>
              <input
                type="text"
                value={formData.organization}
                onChange={(e) => setFormData({...formData, organization: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="e.g., ABC Corporation"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Project Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Describe the project requirements, scope, and objectives..."
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Budget (Optional)
                </label>
                <input
                  type="text"
                  value={formData.budget}
                  onChange={(e) => setFormData({...formData, budget: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="e.g., $50,000 - $100,000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Timeline (Optional)
                </label>
                <input
                  type="text"
                  value={formData.timeline}
                  onChange={(e) => setFormData({...formData, timeline: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="e.g., 6 months"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Specific Requirements (Optional)
              </label>
              <textarea
                value={formData.requirements}
                onChange={(e) => setFormData({...formData, requirements: e.target.value})}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Any specific requirements, compliance needs, or constraints..."
              />
            </div>

            {/* Template Selection */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <FileCheck className="w-4 h-4 inline mr-1" />
                Proposal Template (Optional)
              </label>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100"
              >
                <option value="">Use default structure</option>
                {templates.map((template) => (
                  <option key={template._id} value={template._id}>
                    {template.name} ({template.sections?.length || 0} sections) {template.isDefault ? '⭐' : ''}
                  </option>
                ))}
              </select>
              {selectedTemplateId && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  The proposal will be generated following the selected template's structure
                </p>
              )}
            </div>

            {/* AI Model Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                AI Model
              </label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
              >
                <optgroup label="⭐ Recommended">
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo - Fast & Reliable (~$0.002/1K tokens)</option>
                  <option value="gpt-4o-mini">GPT-4o Mini - Smart & Affordable (~$0.003/1K tokens)</option>
                </optgroup>
                <optgroup label="🚀 OpenAI - Premium">
                  <option value="gpt-4o">GPT-4o - Latest & Best (~$0.03/1K tokens)</option>
                  <option value="gpt-4-turbo">GPT-4 Turbo - High Quality (~$0.03/1K tokens)</option>
                  <option value="gpt-4">GPT-4 - Original (~$0.06/1K tokens)</option>
                  <option value="o1-preview">O1 Preview - Reasoning (~$0.06/1K tokens)</option>
                  <option value="o1-mini">O1 Mini - Fast Reasoning (~$0.012/1K tokens)</option>
                </optgroup>
                <optgroup label="🟣 Anthropic Claude - Latest">
                  <option value="claude-sonnet-4-20250514">Claude Sonnet 4 - Latest Best (~$0.003/1K in, $0.015/1K out)</option>
                  <option value="claude-3-7-sonnet-20250219">Claude 3.7 Sonnet - Extended Thinking (~$0.003/1K tokens)</option>
                  <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet v2 - Balanced (~$0.003/1K tokens)</option>
                  <option value="claude-3-5-haiku-20241022">Claude 3.5 Haiku - Fast & Cheap (~$0.0008/1K tokens)</option>
                </optgroup>
                <optgroup label="🟣 Anthropic Claude - Classic">
                  <option value="claude-3-opus-20240229">Claude 3 Opus - Most Powerful (~$0.015/1K tokens)</option>
                  <option value="claude-3-sonnet-20240229">Claude 3 Sonnet (~$0.003/1K tokens)</option>
                  <option value="claude-3-haiku-20240307">Claude 3 Haiku (~$0.00025/1K tokens)</option>
                </optgroup>
                <optgroup label="🔵 Google Gemini (Free Tier Limited)">
                  <option value="gemini-2.5-flash">Gemini 2.5 Flash - Fast (Free tier: 20/day)</option>
                  <option value="gemini-2.0-flash">Gemini 2.0 Flash (Free tier: 20/day)</option>
                  <option value="gemini-1.5-pro">Gemini 1.5 Pro - Advanced (Free tier: 2/min)</option>
                </optgroup>
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                💡 OpenAI models are recommended for reliability. Claude requires ANTHROPIC_API_KEY. Gemini has strict free tier limits.
              </p>
            </div>

            <button
              type="submit"
              disabled={generating}
              className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 font-medium"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              {generating ? 'Generating Proposal...' : 'Generate Proposal with AI'}
            </button>
          </form>
        </div>
      )}

      {/* Loading State */}
      {generating && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
          <div className="flex flex-col items-center justify-center">
            <div className="spinner mb-4"></div>
            <p className="text-gray-600 mb-2">AI is crafting your proposal...</p>
            <p className="text-sm text-gray-500">This may take 30-60 seconds</p>
          </div>
        </div>
      )}

      {/* Generated Proposal */}
      {proposal && !generating && (
        <div className="space-y-4">
          {/* Action Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-primary-600" />
                <span className="font-medium text-gray-900">Your Generated Proposal</span>
              </div>
              
              <div className="flex gap-2">
                {editing ? (
                  <>
                    <button
                      onClick={handleSave}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </button>
                    <button
                      onClick={() => setEditing(false)}
                      className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setEditing(true)}
                      className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </button>
                    <button
                      onClick={handleDownloadWord}
                      className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Word
                    </button>
                    <button
                      onClick={handleDownloadSimple}
                      className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Text
                    </button>
                  </>
                )}
                <button
                  onClick={() => setProposal(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  New Proposal
                </button>
              </div>
            </div>
          </div>

          {/* Proposal Content */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            {editing ? (
              /* Unified Editor Mode */
              <div className="space-y-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Editing Mode:</strong> Edit the entire proposal content below. Use markdown-style headers (## Section Title) to organize sections.
                  </p>
                </div>
                
                {/* Title Edit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Proposal Title</label>
                  <input
                    type="text"
                    value={proposal.title}
                    onChange={(e) => setProposal({...proposal, title: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-xl font-semibold"
                  />
                </div>

                {/* Executive Summary Edit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Executive Summary</label>
                  <textarea
                    value={proposal.executiveSummary || ''}
                    onChange={(e) => setProposal({...proposal, executiveSummary: e.target.value})}
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Write your executive summary here..."
                  />
                </div>

                {/* All Sections in Single Editor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Proposal Content (All Sections)</label>
                  <textarea
                    value={proposal.sections?.map(s => `## ${s.title}\n\n${s.content}`).join('\n\n') || ''}
                    onChange={(e) => {
                      // Parse sections from the unified content
                      const content = e.target.value
                      const sectionRegex = /## (.+?)\n\n([\s\S]*?)(?=\n\n## |$)/g
                      const newSections = []
                      let match
                      while ((match = sectionRegex.exec(content)) !== null) {
                        newSections.push({
                          title: match[1].trim(),
                          content: match[2].trim(),
                          order: newSections.length
                        })
                      }
                      // If no sections parsed, keep as single section
                      if (newSections.length === 0 && content.trim()) {
                        newSections.push({
                          title: 'Content',
                          content: content.trim(),
                          order: 0
                        })
                      }
                      setProposal({...proposal, sections: newSections.length > 0 ? newSections : proposal.sections})
                    }}
                    rows={20}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                    placeholder="## Section Title

Section content goes here...

## Another Section

More content..."
                  />
                </div>

                {/* Budget & Timeline Edit */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Budget Amount</label>
                    <input
                      type="number"
                      value={proposal.budget?.amount || ''}
                      onChange={(e) => setProposal({
                        ...proposal, 
                        budget: {...(proposal.budget || {}), amount: parseFloat(e.target.value) || 0, currency: proposal.budget?.currency || 'INR'}
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Timeline</label>
                    <input
                      type="text"
                      value={proposal.timeline || ''}
                      onChange={(e) => setProposal({...proposal, timeline: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g., 3 months"
                    />
                  </div>
                </div>
              </div>
            ) : (
              /* View Mode */
              <>
                {/* Cover Page / Header with Logo */}
                <div className="border-b border-gray-200 pb-6 mb-8">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {proposal.companyInfo?.logo && (
                        <img 
                          src={proposal.companyInfo.logo} 
                          alt="Company Logo" 
                          className="h-16 object-contain mb-4"
                        />
                      )}
                      {proposal.companyInfo?.name && (
                        <p className="text-lg font-semibold text-gray-800">{proposal.companyInfo.name}</p>
                      )}
                      {proposal.companyInfo?.address && (
                        <p className="text-sm text-gray-600">{proposal.companyInfo.address}</p>
                      )}
                      {proposal.companyInfo?.contact && (
                        <p className="text-sm text-gray-600">{proposal.companyInfo.contact}</p>
                      )}
                      {proposal.companyInfo?.website && (
                        <p className="text-sm text-blue-600">{proposal.companyInfo.website}</p>
                      )}
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      <p>Date: {new Date(proposal.coverPage?.date || proposal.createdAt).toLocaleDateString()}</p>
                      {proposal.clientInfo?.name && (
                        <p className="mt-1">Submitted to: <span className="font-medium text-gray-700">{proposal.clientInfo.name}</span></p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Title */}
                <h1 className="text-3xl font-bold text-gray-900 mb-6">{proposal.title}</h1>

                {/* Executive Summary */}
                {proposal.executiveSummary && (
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-3">Executive Summary</h2>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{proposal.executiveSummary}</p>
                  </div>
                )}

                {/* Sections */}
                {proposal.sections?.map((section, index) => (
                  <div key={index} className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-3">{section.title}</h2>
                    <div 
                      className="text-gray-700 leading-relaxed whitespace-pre-wrap proposal-content"
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  </div>
                ))}

                {/* Budget */}
                {proposal.budget && (
                  <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-3">Investment</h2>
                    <p className="text-2xl font-bold text-blue-600">
                      {proposal.budget.currency} {proposal.budget.amount?.toLocaleString()}
                    </p>
                  </div>
                )}

                {/* Timeline */}
                {proposal.timeline && (
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-3">Project Timeline</h2>
                    <p className="text-gray-700">{proposal.timeline}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ProposalGenerator
